import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { History, DollarSign, Wrench, User, MapPin, Plus } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

interface MaintenanceRecord {
  id: string
  vehicle_id: string
  vehicle_name: string
  service_type: string
  description: string
  service_date: number
  odometer_at_service: number | null
  cost: number | null
  vendor: string | null
  mechanic: string | null
  status: string
  notes: string | null
}

export function ServiceHistory() {
  const { data, isLoading } = useQuery({
    queryKey: ['maintenance-records'],
    queryFn: async () => {
      const res = await fetch('/api/maintenance/records')
      if (!res.ok) throw new Error('Failed to fetch maintenance records')
      return res.json() as Promise<{ records: MaintenanceRecord[] }>
    },
    refetchInterval: 60000,
  })

  const getServiceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      oil_change: 'Oil Change',
      tire_rotation: 'Tire Rotation',
      brake_service: 'Brake Service',
      inspection: 'Inspection',
      repair: 'Repair',
      other: 'Other'
    }
    return labels[type] || type
  }

  const getServiceTypeIcon = () => {
    // All service types use Wrench icon
    return <Wrench className="h-5 w-5" />
  }

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      completed: 'default',
      scheduled: 'secondary',
      in_progress: 'outline',
      cancelled: 'destructive'
    }
    return variants[status] || 'secondary'
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Service History
          </CardTitle>
          <Button size="sm" variant="outline" className="gap-2">
            <Plus className="h-4 w-4" />
            Add Record
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading service history...
          </div>
        ) : !data?.records || data.records.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <History className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p>No service history available</p>
            <Button size="sm" variant="outline" className="mt-4 gap-2">
              <Plus className="h-4 w-4" />
              Add First Record
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {data.records.map((record, index) => {
              const isRecent = Date.now() / 1000 - record.service_date < 86400 * 7 // Within 7 days

              return (
                <div key={record.id} className="relative">
                  {/* Timeline line */}
                  {index !== data.records.length - 1 && (
                    <div className="absolute left-[15px] top-[40px] bottom-[-16px] w-0.5 bg-border" />
                  )}

                  <Card className={`hover:shadow-md transition-shadow ${isRecent ? 'border-primary' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          isRecent ? 'bg-primary text-primary-foreground' : 'bg-muted'
                        }`}>
                          {getServiceTypeIcon()}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 space-y-2">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{getServiceTypeLabel(record.service_type)}</h3>
                                <Badge variant={getStatusVariant(record.status)}>
                                  {record.status.replace('_', ' ')}
                                </Badge>
                                {isRecent && (
                                  <Badge variant="outline" className="bg-primary/10 text-primary">
                                    Recent
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{record.description}</p>
                            </div>

                            {/* Date */}
                            <div className="text-right">
                              <p className="text-sm font-medium">
                                {new Date(record.service_date * 1000).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(record.service_date * 1000), { addSuffix: true })}
                              </p>
                            </div>
                          </div>

                          {/* Details Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-xs text-muted-foreground">Vehicle</p>
                                <p className="font-medium truncate">{record.vehicle_name}</p>
                              </div>
                            </div>

                            {record.odometer_at_service && (
                              <div>
                                <p className="text-xs text-muted-foreground">Odometer</p>
                                <p className="font-medium">{record.odometer_at_service.toLocaleString()} mi</p>
                              </div>
                            )}

                            {record.cost && (
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="text-xs text-muted-foreground">Cost</p>
                                  <p className="font-medium">${record.cost.toFixed(2)}</p>
                                </div>
                              </div>
                            )}

                            {record.vendor && (
                              <div>
                                <p className="text-xs text-muted-foreground">Vendor</p>
                                <p className="font-medium truncate">{record.vendor}</p>
                              </div>
                            )}

                            {record.mechanic && (
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="text-xs text-muted-foreground">Mechanic</p>
                                  <p className="font-medium truncate">{record.mechanic}</p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Notes */}
                          {record.notes && (
                            <div className="pt-2 border-t">
                              <p className="text-xs text-muted-foreground">Notes:</p>
                              <p className="text-sm mt-1">{record.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
